"use client";

import { useCallback, useEffect, useState } from "react";
import { FormStatus } from "@/components/ui/Form";
import {
  fetchUserGalleries,
  deleteUserGallery,
  type UserGallery,
  type GalleryPagination,
} from "@/lib/api/c-photo-gallery-user";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { GalleryList } from "./GalleryList";
import { GalleryForm } from "./GalleryForm";
import { GalleryImages } from "./GalleryImages";
import styles from "./CPhotoGallery.module.css";

/**
 * Plugin galerii usera (shell c_user_accounts) - REST/React.
 *
 * Akcje hash-routable (z `sharingContentForApi`): `galleries` (lista),
 * `new-gallery` (formularz dodania). Sub-nawigacja (edycja / zarządzanie
 * zdjęciami konkretnej galerii) jest WEWNETRZNYM stanem (`mode`) - ProfileTabs
 * matchuje tylko zarejestrowane akcje, więc dynamiczne id nie przechodza przez hash.
 *
 * Stan `mode`:
 *   - list   -> GalleryList (lista + per-wiersz edytuj/zdjęcia/usun + dodaj)
 *   - create -> GalleryForm (nowa)
 *   - edit   -> GalleryForm (edycja, galleryId)
 *   - images -> GalleryImages (upload + grid + usun + okładka)
 */
type Mode =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; id: number }
  | { type: "images"; id: number; name: string };

export function CPhotoGalleryView({ actionId }: { actionId: string }) {
  const [mode, setMode] = useState<Mode>(
    actionId === "new-gallery" ? { type: "create" } : { type: "list" },
  );

  const [galleries, setGalleries] = useState<UserGallery[]>([]);
  const [pagination, setPagination] = useState<GalleryPagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { galleries: rows, pagination: p } = await fetchUserGalleries(page, 20);
      setGalleries(rows);
      setPagination(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (mode.type === "list") void reload();
  }, [mode, reload]);

  const handleDelete = async (g: UserGallery) => {
    const ok = await confirmDialog({
      title: "Usunąć galerię?",
      message: `Galeria "${g.name}" i wszystkie jej zdjęcia zostaną trwale usunięte.`,
      confirmLabel: "Usuń",
      cancelLabel: "Anuluj",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteUserGallery(g.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (mode.type === "create") {
    return (
      <GalleryForm
        onSaved={(newId) => setMode({ type: "images", id: newId, name: "" })}
        onCancel={() => setMode({ type: "list" })}
      />
    );
  }

  if (mode.type === "edit") {
    return (
      <GalleryForm
        galleryId={mode.id}
        onSaved={() => setMode({ type: "list" })}
        onCancel={() => setMode({ type: "list" })}
      />
    );
  }

  if (mode.type === "images") {
    return (
      <GalleryImages
        galleryId={mode.id}
        onBack={() => setMode({ type: "list" })}
      />
    );
  }

  // list
  return (
    <div className={styles.root}>
      {error && <FormStatus variant="error">{error}</FormStatus>}
      <GalleryList
        galleries={galleries}
        pagination={pagination}
        page={page}
        loading={loading}
        onPageChange={setPage}
        onNew={() => setMode({ type: "create" })}
        onEdit={(g) => setMode({ type: "edit", id: g.id })}
        onImages={(g) => setMode({ type: "images", id: g.id, name: g.name })}
        onDelete={handleDelete}
      />
    </div>
  );
}

/**
 * Czy plugin obsługuje daną akcję (registry w ExtensionActionView).
 * Tylko akcje hash-routable z `sharingContentForApi`.
 */
export function cPhotoGalleryHandlesAction(actionId: string): boolean {
  return actionId === "galleries" || actionId === "new-gallery";
}
